import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Layers, Check, ExternalLink } from 'lucide-react';
import { listEsteiras } from '@/services/api/utility-esteiras';
import { getUtilityTemplateGroupsAll } from '@/services/api/broadcast';
import UtilityOverlapResolver from '../../UtilityEsteiras/components/UtilityOverlapResolver';
import type { MultiTemplateSelectionEntry } from '../../UtilityEsteiras/types';

interface StepSelectEsteiraProps {
    selectedEsteiraId: number | null;
    onSelectedEsteiraChange: (id: number | null) => void;
}

/**
 * Etapa dedicada "Esteira": lista as Esteiras salvas do usuário e, ao
 * escolher uma, REVALIDA a cobertura em tempo real (não confia no snapshot
 * gravado na Esteira) — cada página pode ter perdido aprovação desde que a
 * Esteira foi salva. Mesma limitação documentada em
 * PRD-esteira-multi-template-utility.md, seção 8, risco 1 — resolvida aqui.
 */
export default function StepSelectEsteira({ selectedEsteiraId, onSelectedEsteiraChange }: StepSelectEsteiraProps) {
    const { data: esteiras = [], isLoading: loadingEsteiras } = useQuery({
        queryKey: ['utility-esteiras'],
        queryFn: listEsteiras,
        staleTime: 1000 * 60 * 5,
    });

    const { data: allUtilityGroups = [], isLoading: loadingGroups } = useQuery({
        queryKey: ['utility-groups'],
        queryFn: getUtilityTemplateGroupsAll,
        staleTime: 1000 * 60 * 5,
    });

    const templatesById = useMemo(() => new Map(allUtilityGroups.map(g => [g.id, g])), [allUtilityGroups]);

    const selectedEsteira = esteiras.find(e => e.id === selectedEsteiraId) ?? null;

    const selection: MultiTemplateSelectionEntry[] = useMemo(() => {
        if (!selectedEsteira) return [];
        return selectedEsteira.template_resolution.map((entry, idx) => {
            const group = templatesById.get(entry.group_id);
            return {
                groupId: entry.group_id,
                priority: idx,
                templateName: group?.template_name ?? `Template #${entry.group_id}`,
                title: group?.title,
                imageUrl: group?.image_url ?? null,
                thumbnailUrl: group?.thumbnail_url ?? null,
                approvedCount: group?.approved_count,
                secondaryGroupId: entry.secondary_group_id ?? null,
            };
        });
    }, [selectedEsteira, templatesById]);

    const loading = loadingEsteiras || loadingGroups;

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#7dd3fc] mb-4" />
                <p className="text-gray-600">Carregando Esteiras...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="text-center mb-2">
                <h3 className="text-xl font-semibold text-gray-900">Selecione a Esteira</h3>
                <p className="text-sm text-gray-600 mt-1">
                    Pack de páginas + templates já montado — validamos a cobertura em tempo real antes de avançar
                </p>
            </div>

            {esteiras.length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                    <Layers className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Nenhuma Esteira criada ainda</p>
                    <a
                        href="/meta/utility-esteiras/new"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 mt-3 text-sm text-[#0f172a] hover:underline"
                    >
                        Criar uma Esteira <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
            ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {esteiras.map(esteira => {
                        const isSelected = selectedEsteiraId === esteira.id;
                        return (
                            <button
                                key={esteira.id}
                                type="button"
                                onClick={() => onSelectedEsteiraChange(esteira.id)}
                                className={`relative w-full p-4 border-2 rounded-xl text-left transition-all hover:border-[#7dd3fc] hover:shadow-md ${
                                    isSelected ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md' : 'border-gray-200 bg-white'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <Layers className="w-5 h-5 text-gray-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-gray-900 truncate">{esteira.name}</h4>
                                        <p className="text-xs text-gray-500">
                                            {esteira.page_ids.length} página(s) · {esteira.template_resolution.length} template(s)
                                        </p>
                                    </div>
                                    {isSelected && <Check className="w-4 h-4 text-[#7dd3fc] shrink-0" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {selectedEsteira && selection.length > 0 && (
                <UtilityOverlapResolver
                    selection={selection}
                    pageIds={selectedEsteira.page_ids}
                    templatesById={templatesById}
                    conflictResolutions={{}}
                    onConflictResolutionsChange={() => {}}
                    onReadyChange={() => {}}
                />
            )}
        </div>
    );
}
